import { css } from '@emotion/react';
import { forwardRef } from 'react';

import { WeblogPageCategory, WeblogScreenName } from '../../../constants/weblog';
import DomainManager from '../../../utils/DomainManager';
import { logBrazeEvent } from '@myrealtrip/marketing-log';
import { helper } from '@myrealtrip/frontend-weblog';
import { weblog } from '@myrealtrip/logger';
import { useDetermineMobile } from '@myrealtrip/device-detect';
import styled from '@emotion/styled';
import Icon from '../../../designSystem/components/Icon';
import { globalVariables } from '../../../designSystem/constants';
import { useEnvContext } from '../../../../modules/EnvProvider';
import { useLogData } from '../LogDataContext';

const { LoggingImpression } = weblog;

const { sendSearchLog } = helper;

const baseSearchLog = {
  eventName: 'gsearch',
  itemKind: 'recent_keyword',
  screenName: WeblogScreenName.COMMON,
  pageCategory: WeblogPageCategory.COMMON,
};

interface Props {
  keyword: string;
  index: number;
  isSelected: boolean;
  onDelete?: (keyword: string) => void;
}

const RecentKeywordItem = forwardRef<HTMLAnchorElement, Props>(
  ({ keyword, index, isSelected, onDelete }, ref) => {
    const { targetEnv } = useEnvContext();
    const isMobile = useDetermineMobile();
    const logData = useLogData();
    const domainManager = new DomainManager(targetEnv);

    const targetUrl = `${domainManager.getTravelerServiceDomain()}/search-bridge?q=${encodeURIComponent(
      keyword,
    )}`;

    const sendLog = () => {
      try {
        logBrazeEvent('search_offer', {
          search_offer: keyword,
        });

        sendSearchLog({
          ...baseSearchLog,
          searchWord: keyword,
          targetUrl,
          screenName: logData?.screenName ?? baseSearchLog.screenName,
          pageCategory: logData?.pageCategory ?? baseSearchLog.pageCategory,
          ...logData?.params,
        });
      } catch (e) {
        // ignore
      }
    };

    const handleDelete = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete?.(keyword);
    };

    return (
      <LoggingImpression
        screenName={logData?.screenName ?? WeblogScreenName.COMMON}
        eventName="gsearch"
        pageCategory={logData?.pageCategory ?? WeblogPageCategory.COMMON}
        params={{
          itemKind: 'text',
          keyword,
          columnName: '최근검색어',
          columnCode: 'recent_keywords',
          columnIndex: isMobile ? 0 : 1,
          itemVerticalIndex: index,
          ...logData?.params,
        }}
        impressionOptions={{ key: `recent_keyword_${keyword}`, threshold: 0.5 }}
      >
        <Anchor href={targetUrl} ref={ref} isSelected={isSelected} onClick={sendLog}>
          <ClockIcon name="ic_clock_sm_filled_gray_500" alt="" />
          <Label>{keyword}</Label>
          {onDelete && (
            <DeleteButton type="button" onClick={handleDelete} aria-label={`${keyword} 삭제`}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5"
                  stroke={globalVariables.gray_400}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </DeleteButton>
          )}
        </Anchor>
      </LoggingImpression>
    );
  },
);

RecentKeywordItem.displayName = 'RecentKeywordItem';

export default RecentKeywordItem;

const Anchor = styled.a<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${globalVariables.gray_50};
    `};

  :hover {
    background-color: ${globalVariables.gray_50};
  }

  @media (max-width: 768px) {
    padding: 6px 6px 6px 10px;
    border: 1px solid ${globalVariables.gray_200};
    border-radius: 16px;
    background-color: ${globalVariables.white};

    :hover {
      background-color: ${globalVariables.white};
    }
  }
`;

const ClockIcon = styled(Icon)`
  margin-right: 8px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Label = styled.span`
  flex: 1;
  font-size: 15px;
  color: ${globalVariables.gray_700};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    flex: none;
    font-size: 13px;
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
  }
`;

const DeleteButton = styled.button`
  margin: 0;
  padding: 2px;
  appearance: none;
  border: none;
  outline: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: auto;

  @media (max-width: 768px) {
    margin-left: 4px;
  }
`;
